import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import { FormatMoneyLocal } from 'eco-design-micro/price/FormatMoneyLocal';
import EcoSpace from 'eco-design/base/eco-space';
import { useRef, useEffect, useState, Fragment, useMemo, useCallback, memo } from 'react';
import { useRouter } from 'next/router';
import css from './index.module.scss';
import { getSolarRecommand } from '@/service/solar-calculator';
import gtm from '@/utils/gtm';
import { useLang } from '@/i18n';
import { getIsPadWidth, getIsMobileWidth2 } from '@elem/utils/devices';

const ICON_1 = memo(() => (
    <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.734375" y="0.199219" width="28" height="28" rx="14" fill="#E6E6E7" />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.3525 14.8352H19.1145C19.3216 14.8352 19.4916 14.7756 19.6247 14.6562C19.7578 14.5369 19.8244 14.3845 19.8244 14.1989C19.8244 14.0133 19.7578 13.8608 19.6247 13.7415C19.4916 13.6222 19.3216 13.5625 19.1145 13.5625H10.3525C10.1454 13.5625 9.97534 13.6222 9.84223 13.7415C9.70913 13.8608 9.64258 14.0133 9.64258 14.1989C9.64258 14.3845 9.70913 14.5369 9.84223 14.6562C9.97534 14.7756 10.1454 14.8352 10.3525 14.8352Z"
            fill="#83868A"
        />
    </svg>
));

const ICON_2 = memo(() => (
    <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.734375" y="0.199219" width="28" height="28" rx="14" fill="#E6E6E7" />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.3525 14.9092H14.0236V18.5803C14.0236 18.7874 14.0902 18.9575 14.2233 19.0906C14.3564 19.2237 14.5264 19.2902 14.7335 19.2902C14.9405 19.2902 15.1106 19.2237 15.2437 19.0906C15.3768 18.9575 15.4434 18.7874 15.4434 18.5803V14.9092H19.1145C19.3216 14.9092 19.4916 14.8426 19.6247 14.7095C19.7578 14.5764 19.8244 14.4064 19.8244 14.1993C19.8244 13.9923 19.7578 13.8222 19.6247 13.6891C19.4916 13.556 19.3216 13.4894 19.1145 13.4894H15.4434V9.81829C15.4434 9.61124 15.3768 9.44116 15.2437 9.30806C15.1106 9.17495 14.9405 9.1084 14.7335 9.1084C14.5264 9.1084 14.3564 9.17495 14.2233 9.30806C14.0902 9.44116 14.0236 9.61124 14.0236 9.81829V13.4894H10.3525C10.1454 13.4894 9.97534 13.556 9.84223 13.6891C9.70913 13.8222 9.64258 13.9923 9.64258 14.1993C9.64258 14.4064 9.70913 14.5764 9.84223 14.7095C9.97534 14.8426 10.1454 14.9092 10.3525 14.9092Z"
            fill="#83868A"
        />
    </svg>
));

function SectionH() {
    const { t } = useLang('theProduct.SectionH');
    const { locale = 'jp' } = useRouter();
    const sectionRef = useRef<HTMLDivElement>(null);
    const [img, setImg] = useState(['us', 'ca'].includes(locale) ? 2 : 1);
    const [top, setTop] = useState(0);
    const [active, setActive] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    const tabs = useMemo(() => ['tab1_label', 'tab2_label', 'tab3_label'], []);

    const tabsImg = useMemo(
        () => [
            {
                pc: 'https://websiteoss.ecoflow.com/upload/image/1761796656523/sectionh_1.jpg',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1761796656500/sectionh_1_h5.jpg',
            },
            {
                pc: 'https://websiteoss.ecoflow.com/upload/image/1761796656560/sectionh_2.jpg',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1761796711182/sectionh_2_h5.jpg',
            },
            {
                pc: 'https://websiteoss.ecoflow.com/upload/image/1762152236749/sectionh_3.jpg',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1761796656508/sectionh_3_h5.jpg',
            },
        ],
        [],
    );

    const [curProducts, setCurProducts] = useState([]);

    const productsAll: any = useMemo(
        () => [
            [
                {
                    name: t('tab1_item1_name'),
                    power: t('tab1_item1_power'),
                    desc: t('tab1_item1_desc'),
                    img: 'https://websiteoss.ecoflow.com/upload/image/1761718564645/sectionh1_1.png',
                    learn_more: (() => {
                        const map: any = {
                            jp: 'https://jp.ecoflow.com/products/delta-3-1000-air-alternator-charger-600',
                        };
                        return map[locale] || '';
                    })(),
                    question: t('F_tab1_card1_sub_question'),
                    productId: (() => {
                        const map: any = {
                            jp: '14793604858223',
                        };
                        return map[locale] || '';
                    })(),
                    variantId: (() => {
                        const map: any = {
                            jp: '52235441635695',
                        };
                        return map[locale] || '';
                    })(),
                },
                {
                    name: t('tab1_item2_name'),
                    power: t('tab1_item2_power'),
                    desc: t('tab1_item2_desc'),
                    question: t('tab1_item2_question'),
                    img: 'https://websiteoss.ecoflow.com/upload/image/1761718564702/sectionh1_2.png',

                    learn_more: (() => {
                        const map: any = {
                            jp: 'https://jp.ecoflow.com/products/delta-3-plus-alternator-charger-600',
                        };
                        return map[locale] || '';
                    })(),
                    productId: (() => {
                        const map: any = {
                            jp: '14793605710191',
                        };
                        return map[locale] || '';
                    })(),
                    variantId: (() => {
                        const map: any = {
                            jp: '52235457724783',
                        };
                        return map[locale] || '';
                    })(),
                    sub: {
                        id: 'AC',
                        name: t('tab1_item2_sub1_name'),
                        parentVariantId: (() => {
                            const map: any = {
                                jp: '52235457724783',
                            };
                            return map[locale] || '';
                        })(),
                        productId: (() => {
                            const map: any = {
                                jp: '14794729521519',
                            };
                            return map[locale] || '';
                        })(),
                        variantId: (() => {
                            const map: any = {
                                jp: '52242011193711',
                            };
                            return map[locale] || '';
                        })(),
                        learn_more: (() => {
                            const map: any = {
                                jp: 'https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt150output-new-1m',
                            };
                            return map[locale] || '';
                        })(),
                    },
                },
            ],
            [
                {
                    name: t('tab2_item1_name'),
                    power: t('tab2_item1_power'),
                    desc: t('tab2_item1_desc'),
                    img: 'https://websiteoss.ecoflow.com/upload/image/1761718566526/sectionh2_1.png',

                    learn_more: (() => {
                        const map: any = {
                            jp: 'https://jp.ecoflow.com/products/delta-3-max-plus-800w-alternator-charger',
                        };
                        return map[locale] || '';
                    })(),
                    question: t('tab2_item1_question'),

                    productId: (() => {
                        const map: any = {
                            jp: '14776030986607',
                        };
                        return map[locale] || '';
                    })(),
                    variantId: (() => {
                        const map: any = {
                            jp: '52158000202095',
                        };
                        return map[locale] || '';
                    })(),
                    sub: {
                        id: '400w',
                        name: t('tab2_item1_sub1_name'),
                        parentVariantId: (() => {
                            const map: any = {
                                jp: '52158000202095',
                            };
                            return map[locale] || '';
                        })(),
                        productId: (() => {
                            const map: any = {
                                jp: '14702400930159',
                            };
                            return map[locale] || '';
                        })(),
                        variantId: (() => {
                            const map: any = {
                                jp: '51903301484911',
                            };
                            return map[locale] || '';
                        })(),
                        learn_more: (() => {
                            const map: any = {
                                jp: 'https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt60output-1m',
                            };
                            return map[locale] || '';
                        })(),
                    },
                },
                {
                    name: t('tab2_item2_name'),
                    power: t('tab2_item2_power'),
                    desc: t('tab2_item2_desc'),
                    img: 'https://websiteoss.ecoflow.com/upload/image/1761718564519/sectionh2_2.png',

                    learn_more: (() => {
                        const map: any = {
                            jp: 'https://jp.ecoflow.com/products/delta-3-ultra-plus-alternator-charger-800',
                        };
                        return map[locale] || '';
                    })(),
                    question: t('tab2_item2_question'),
                    productId: (() => {
                        const map: any = {
                            jp: '14793605808495',
                        };
                        return map[locale] || '';
                    })(),
                    variantId: (() => {
                        const map: any = {
                            jp: '52235460936047',
                        };
                        return map[locale] || '';
                    })(),
                    sub: {
                        id: 'AC',
                        name: t('tab2_item2_sub1_name'),
                        parentVariantId: (() => {
                            const map: any = {
                                jp: '52235460936047',
                            };
                            return map[locale] || '';
                        })(),
                        productId: (() => {
                            const map: any = {
                                jp: '14702400930159',
                            };
                            return map[locale] || '';
                        })(),
                        variantId: (() => {
                            const map: any = {
                                jp: '51903301484911',
                            };
                            return map[locale] || '';
                        })(),
                        learn_more: (() => {
                            const map: any = {
                                jp: 'https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt60output-1m',
                            };
                            return map[locale] || '';
                        })(),
                    },
                },
            ],
            [
                {
                    name: t('tab3_item1_name'),
                    power: t('tab3_item1_power'),
                    desc: t('tab3_item1_desc'),
                    img: 'https://websiteoss.ecoflow.com/upload/image/1761718566525/sectionh3_1.png',

                    learn_more: (() => {
                        const map: any = {
                            jp: 'https://jp.ecoflow.com/products/delta-pro-alternator-charger-plus-1000',
                        };
                        return map[locale] || '';
                    })(),
                    question: t('tab3_item1_question'),
                    productId: (() => {
                        const map: any = {
                            jp: '14793604989295',
                        };
                        return map[locale] || '';
                    })(),
                    variantId: (() => {
                        const map: any = {
                            jp: '52235448058223',
                        };
                        return map[locale] || '';
                    })(),
                    sub: {
                        id: '400w',
                        name: t('tab3_item1_sub1_name'),
                        parentVariantId: (() => {
                            const map: any = {
                                jp: '52235448058223',
                            };
                            return map[locale] || '';
                        })(),
                        productId: (() => {
                            const map: any = {
                                jp: '14794729554287',
                            };
                            return map[locale] || '';
                        })(),
                        variantId: (() => {
                            const map: any = {
                                jp: '52242011226479',
                            };
                            return map[locale] || '';
                        })(),
                        learn_more: (() => {
                            const map: any = {
                                jp: 'https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt60output',
                            };
                            return map[locale] || '';
                        })(),
                    },
                },
                {
                    name: t('tab3_item2_name'),
                    power: t('tab3_item2_power'),
                    desc: t('tab3_item2_desc'),
                    img: 'https://websiteoss.ecoflow.com/upload/image/1761718565762/sectionh3_2.png',

                    learn_more: (() => {
                        const map: any = {
                            jp: 'https://jp.ecoflow.com/products/delta-pro-3-alternator-charger-plus-1000',
                        };
                        return map[locale] || '';
                    })(),
                    question: t('tab3_item2_question'),
                    productId: (() => {
                        const map: any = {
                            jp: '14793605939567',
                        };
                        return map[locale] || '';
                    })(),
                    variantId: (() => {
                        const map: any = {
                            jp: '52235469717871',
                        };
                        return map[locale] || '';
                    })(),
                    sub: {
                        id: 'AC',
                        name: t('tab3_item2_sub1_name'),
                        parentVariantId: (() => {
                            const map: any = {
                                jp: '52235469717871',
                            };
                            return map[locale] || '';
                        })(),
                        productId: (() => {
                            const map: any = {
                                jp: '14794729554287',
                            };
                            return map[locale] || '';
                        })(),
                        variantId: (() => {
                            const map: any = {
                                jp: '52242011226479',
                            };
                            return map[locale] || '';
                        })(),
                        learn_more: (() => {
                            const map: any = {
                                jp: 'https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt60output',
                            };
                            return map[locale] || '';
                        })(),
                    },
                },
            ],
        ],
        [t, locale],
    );

    const [requestedArr, setRequestedArr] = useState(() => new Array(3).fill(null));

    const handleClickTab = useCallback(
        (tab: string, k: number) => {
            gtm.push({
                event: 'uaEvent',
                event_name: 'tab_click',
                list_name: 'none',
                button_name: t(`tab${k + 1}_label_gtm`),
                __btn_id1: `RV-sectionH .tab-${k + 1}`,
                __btn_id2: `tab-${k + 1}`,
                __btn_type: 'common',
            });

            setActive(k);
            setCurProducts([]); // 立即清空产品列表，避免显示旧数据
            setImg(k + 1);
            const headerBox = document.querySelector('.SectionH .header-box') as HTMLDivElement;
            headerBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
            const tabBox = document.querySelector('.SectionH .tabs-box') as HTMLDivElement;
            const { scrollWidth, clientWidth } = tabBox;
            if (scrollWidth > window?.innerWidth) {
                if (window.innerWidth >= 768) {
                    if (k >= tabs?.length / 2) {
                        tabBox.scrollLeft = scrollWidth - clientWidth;
                    } else {
                        tabBox.scrollLeft = 0;
                    }
                }
                if (window?.innerWidth < 768) {
                    const tab = tabBox?.querySelector('.tab') as HTMLUListElement;
                    tabBox.scrollLeft = tab?.offsetWidth * k + (k + 1 >= tabs?.length ? 0 : 12);
                }
            }
        },
        [t, tabs?.length],
    );

    const handleBuy = useCallback(
        (product: any) => {
            let frag = `https://${locale}.ecoflow.com/cart/${product.variantId}:1`;
            if (product?.sub && product?.sub?.quantity > 0) {
                frag += `,${product?.sub?.variantId}:${product?.sub?.quantity}`;
            }
            frag += '?channel=buy_button';

            gtm.push({
                event: 'uaEvent',
                event_name: 'checkout',
                button_name: t('btn_buy_now'),
                __btn_id1: `RV-sectionH .buy-now`,
                __btn_id2: frag,
                __btn_type: 'href',
            });
            window.open(frag, '_blank');
        },
        [locale, t],
    );

    const handlerLearnMore = useCallback((product: any) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'buy',
            product_name: product.name,
            position: `Learn More ${product.name}`,

            __btn_id1: `RV-sectionH .learn-more`,
            __btn_id2: product.learn_more,
            __btn_type: 'href',
        });
        window.open(product.learn_more, '_blank');
    }, []);

    const handleCalculate = useCallback(
        (data: any, n: number) => {
            const tempBundles = JSON.parse(JSON.stringify(curProducts));
            const parentIdx = tempBundles?.findIndex((bundle: any) => bundle?.variantId === data?.parentVariantId);
            tempBundles[parentIdx].sub.quantity = n;
            setCurProducts(tempBundles);
        },
        [curProducts],
    );

    const getAsynItem = useCallback(
        async (item: any) => {
            const { productId, variantId } = item;
            if (productId && variantId) {
                return await new Promise(async (resolve, reject) => {
                    try {
                        const res = await getSolarRecommand({ productId, variantId }, locale);
                        if (res?.code === '0') {
                            item.discountPrice = res?.data?.price;
                            item.originalPrice = res?.data?.compareAtPrice || res?.data?.price;
                            item.inventory = res?.data?.inventoryQuantity ?? undefined;
                            if (!item?.img && res?.data?.img) {
                                item.img = item?.pc || res?.data?.img;
                            }

                            // item.inventory = 100;
                            if (item.id) {
                                item.quantity = 0;
                            }
                            if (item?.sub) {
                                await getAsynItem(item?.sub);
                            }
                            return resolve(res);
                        } else {
                            console.error(res?.message);
                            return reject(res?.message);
                        }
                    } catch (e) {
                        console.error(e);
                        return reject(e);
                    }
                });
            }
            return;
        },
        [locale],
    );

    // 使用 Intersection Observer 监听组件是否接近视口
    useEffect(() => {
        const currentRef = sectionRef.current;
        if (!currentRef || isVisible) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // 当组件距离视口顶部 200px 时触发
                    if (entry.isIntersecting && !isVisible) {
                        setIsVisible(true);
                    }
                });
            },
            {
                // rootMargin: 距离视口顶部 200px 时触发
                rootMargin: '200px 0px 0px 0px',
                threshold: 0,
            },
        );

        observer.observe(currentRef);

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [isVisible]);

    // 只有在组件可见后才请求数据
    useEffect(() => {
        if (img > 0 && isVisible) {
            if (!requestedArr[img - 1]) {
                const groupProducts = JSON.parse(JSON.stringify(productsAll[img - 1]));
                const promises = groupProducts?.map((item: any) => getAsynItem(item));
                Promise.all(promises)
                    .then(() => {
                        const result = groupProducts;
                        setCurProducts(result);
                        const newArr = JSON.parse(JSON.stringify(requestedArr));
                        newArr[img - 1] = JSON.parse(JSON.stringify(result));
                        setRequestedArr(newArr);
                    })
                    .catch((e) => {
                        console.error(e);
                    });
            } else {
                setCurProducts(requestedArr[img - 1]);
            }
        }
    }, [img, isVisible]);

    useEffect(() => {
        // 延迟计算，避免阻塞主线程
        const timer = setTimeout(() => {
            const isPad = getIsPadWidth();
            const isMobile = getIsMobileWidth2();
            let h = 0;
            const offsetH = isMobile ? 110 : isPad ? 86 : 50;
            const bar = document.querySelector('.announcement-bar') as HTMLElement;
            if (bar) {
                const styles = window.getComputedStyle(bar);
                const position = styles.position;
                if (bar && position === 'sticky') {
                    h += bar?.offsetHeight;
                }
            }
            const tab = document.querySelector('.RV-Tab') as HTMLElement;
            if (tab) {
                const styles = window.getComputedStyle(tab);
                const position = styles.position;
                if (tab && position === 'sticky') {
                    h += tab?.offsetHeight;
                }
            }
            setTop(h + offsetH);
        }, 0);

        return () => clearTimeout(timer);
    }, []);

    const AddReduceCalc = memo(({ data, callback }: any) => {
        const NUM_MIN = data?.min || 0;
        const NUM_MAX = data?.inventory > 0 ? data?.inventory : 0;
        const [number, setNumber] = useState(data?.quantity ?? 0);

        const reduceNumber = () => {
            if (number <= NUM_MIN) return false;
            setNumber(number - 1);
            callback(data, number - 1);
            try {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'bundle_remove_from_cart',
                    product_name: data?.name,
                    price: data?.discountPrice,
                    quantity: number - 1,
                    button_name: '-',
                    list_name: 'none',
                    tag: 'none',
                });
            } catch (e) {
                console.error(e);
            }
        };

        const addNumber = () => {
            if (number >= NUM_MAX) {
                return false;
            }
            setNumber(number + 1);
            callback(data, number + 1);
            try {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'bundle_add_to_cart',
                    product_name: data?.name,
                    price: data?.discountPrice,
                    quantity: number + 1,
                    button_name: '+',
                    list_name: 'none',
                    tag: 'none',
                });
            } catch (e) {
                console.error(e);
            }
        };

        const inputChange = (e: any) => {
            const v = e.target.value.replace(/[^0-9]/g, '');
            if (v < NUM_MIN || v > NUM_MAX) {
                return;
            }
            setNumber(Number(v));
            callback(data, Number(v));
        };

        return (
            <div className="bundle-calculator">
                <div className={number <= NUM_MIN ? 'addSubtractor-reduce disable' : 'addSubtractor-reduce'} onClick={reduceNumber}>
                    <ICON_1 />
                </div>
                <input className="addSubtractor-number" value={number} onChange={inputChange} />
                <div className={number >= NUM_MAX ? 'addSubtractor-add disable' : 'addSubtractor-add'} onClick={addNumber}>
                    <ICON_2 />
                </div>
            </div>
        );
    });

    return (
        <div
            id="SectionH"
            className={`SectionH RV-sectionH  eco-content  ${css['SectionH']} GtmModuleData-section`}
            ref={sectionRef}
            data-section-id="on-the-road-SectionH"
        >
            <EcoSpace />

            <div className="header-box">
                <p className="head-title">{t('title')}</p>
            </div>
            <div className="tabs-wrapper" style={{ top: `${top}px` }}>
                <div className="tabs-box">
                    <ul className="tabs">
                        {tabs?.map((tab: string, k: number) => (
                            <li
                                className={`tab ${active === k ? 'active' : ''} GtmModuleData-btn-common`}
                                data-btn-id={`tab-${k + 1}`}
                                key={tab}
                                onClick={() => handleClickTab(tab, k)}
                            >
                                {t.rich(tab, { day: (text: string) => <div className="tab-day">{text}</div> })}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="img-box">
                <EcoImg key={active} pc={tabsImg[active].pc} h5={tabsImg[active].h5} isOpenLazy />
            </div>
            <ul className="products">
                {curProducts?.map((product: any, k: number) => (
                    <Fragment key={`${active}_${k}`}>
                        <li className="product">
                            <div className="product-content">
                                <div className="product-img">
                                    <EcoImg key={`product-img-${k}-${active}`} pc={product?.img} h5={product?.img} isOpenLazy />
                                </div>
                                <div className="product-info">
                                    <p className="product-name">{product.name}</p>
                                    <p className="product-desc">{product.desc}</p>
                                    <p className="product-power">{product.power}</p>
                                    {product?.sub && (
                                        <div className="product-sub">
                                            {product.question && <p className="product-question">{product.question}</p>}
                                            <div className="sub-box">
                                                <EcoLink href={product.sub.learn_more} target="_blank">
                                                    <div className="sub-img">
                                                        <EcoImg key={`sub-img-${k}-${active}`} pc={product?.sub?.img} h5={product?.sub?.img} isOpenLazy />
                                                    </div>
                                                </EcoLink>
                                                {product.question && <p className="product-question-h5">{product.question}</p>}

                                                <div className="sub-info">
                                                    <EcoLink href={product.sub.learn_more} target="_blank">
                                                        <p className="sub-name">{product.sub.name}</p>
                                                    </EcoLink>
                                                    <div className="sub-price">
                                                        <span className="price-discount">{FormatMoneyLocal(product.sub.discountPrice, locale)}</span>
                                                        {product.sub.discountPrice !== product.sub.originalPrice && (
                                                            <span className="price-original">{FormatMoneyLocal(product.sub.originalPrice, locale)}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <AddReduceCalc data={product.sub} callback={handleCalculate} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="product-bottom">
                                <button className={`learn-more GtmModuleData-btn-href`} onClick={() => handlerLearnMore(product)}>
                                    {t('btn_learn_more')}
                                </button>
                                <button
                                    className={`buy-now ${product?.inventory <= 0 ? 'disable' : ''} GtmModuleData-btn-href`}
                                    onClick={product?.inventory <= 0 ? undefined : () => handleBuy(product)}
                                >
                                    {t('btn_buy_now')}
                                </button>
                            </div>
                        </li>
                    </Fragment>
                ))}
            </ul>
        </div>
    );
}

export default memo(SectionH);
